import React, { useState, useEffect } from "react";
import styles from "./AcceptTermsConditions.module.scss";
import Grid from "@mui/material/Grid";
import Error from "../../imgs/error/error.png";
import { InfoIconWithMessage } from "../../shared/infoIcon/InfoIcon";
import { withTranslation } from "react-i18next";
import { base64FileDownload } from "../../utils/FileDownloadUtil";
import TermsAndConditionsClient from "../../shared/clients/TermsAndConditionsClient";

const RenderPreview = (props) => {
  const handleTermsAndConditionsDownload = () => {
    base64FileDownload(props.file, "application/pdf", "terms-conditions.pdf");
  };

  const { Preview } = props;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <h3 className={styles.previewTitle}>
          {props.t("AcceptTermsConditions.termsHeader")}
        </h3>
        <Preview file={props.file} className={styles.previewPDF} />
        <div
          className={`${styles.download} ${styles.downloadPointer} ${styles.downloadFile} `}
        >
          <a
            download="terms-conditions.pdf"
            href={null}
            onClick={handleTermsAndConditionsDownload}
            className={`${styles.download}`}
          >
            {props.t("AcceptTermsConditions.download")}
          </a>
        </div>
      </Grid>
    </Grid>
  );
};

const RenderPreviewT = withTranslation("emp")(RenderPreview);

const AcceptanceSelector = (props) => {
  let pendingText,
    acceptedText,
    rejectedText,
    pendingLabelStyle = styles.pendingLabelStyle;

  if (props.currentStatus === "yes") {
    acceptedText = "";
    pendingText = props.t("AcceptTermsConditions.yes");
    rejectedText = "";
    pendingLabelStyle = "";
  } else if (props.currentStatus === "no") {
    acceptedText = "";
    pendingText = props.t("AcceptTermsConditions.no");
    rejectedText = "";
  } else if (props.currentStatus === "pending") {
    acceptedText = props.t("AcceptTermsConditions.no");
    pendingText = "";
    rejectedText = props.t("AcceptTermsConditions.yes");
    pendingLabelStyle = "";
  }

  const selectedStyle =
    props.currentStatus === "yes" ? styles.labelYes : styles.labelNo;

  return (
    <div className={`${styles.acceptanceSelector} pagePadding`}>
      <span className="primary-text-bold-color">
        {props.t("AcceptTermsConditions.accept")}
      </span>
      <div className={`${styles.switchToggle} switch-3 switch-candy`}>
        <div>
          <input
            className={styles.toggleInput}
            id="off"
            data-testid="off"
            name="state-d"
            type="radio"
            value="no"
            onChange={(e) => props.onSelect(e)}
            checked={props.currentStatus === "no"}
          />
          <label
            className={`${styles.toggleLabel} ${styles.labelNo}`}
            htmlFor="off"
          >
            {acceptedText}
          </label>
        </div>

        <div>
          <input
            className={styles.toggleInput}
            id="na"
            name="state-d"
            type="radio"
            value="pending"
            onChange={(e) => props.onSelect(e)}
            checked={props.currentStatus === "pending"}
          />
          <label
            className={`${styles.toggleLabel} ${selectedStyle} ${pendingLabelStyle}`}
            htmlFor="na"
          >
            {pendingText}
          </label>
        </div>

        <div>
          <input
            className={styles.toggleInput}
            id="on"
            data-testid="on"
            name="state-d"
            type="radio"
            value="yes"
            onChange={(e) => props.onSelect(e)}
            checked={props.currentStatus === "yes"}
          />
          <label
            className={`${styles.toggleLabel} ${styles.labelYes}`}
            htmlFor="on"
          >
            {rejectedText}
          </label>
        </div>
      </div>
    </div>
  );
};

const AcceptanceSelectorT = withTranslation("emp")(AcceptanceSelector);

const Consequences = (props) => {
  if (props.status === "no") {
    return (
      <div id="consequences" className={`${styles.notAcceptMsg}`}>
        <img src={Error} alt="Error" className={styles.Error} />
        {props.t("AcceptTermsConditions.warning")}
      </div>
    );
  } else {
    return <div className={styles.notAcceptMsg}>&nbsp;</div>;
  }
};

const ConsequencesT = withTranslation("emp")(Consequences);

const AcceptanceForm = (props) => {
  const [status, setStatus] = useState("pending");

  useEffect(() => {
    props.showActionFooter(status !== "pending");
  }, []); // Only run on mount

  const onSelectHandler = (e) => {
    const termsStatus = e.target.value;

    if (termsStatus === "pending") {
      setStatus("pending");
      props.updateParentState({ status: "pending" });
      props.showActionFooter(false);
    }
    if (termsStatus === "no") {
      setStatus("no");
      props.updateParentState({ status: "no" });
      props.showActionFooter(true);
    }
    if (termsStatus === "yes") {
      setStatus("yes");
      props.updateParentState({ status: "yes" });
      props.showActionFooter(true);
    }
  };

  return (
    <div>
      <AcceptanceSelectorT
        currentStatus={status}
        onSelect={(e) => onSelectHandler(e)}
      />
      {status === "no" ? <ConsequencesT status={status} /> : null}
    </div>
  );
};

const AcceptTermsConditions = (props) => {
  const [file, setFile] = useState(null);
  const [fileFound, setFileFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTermsConditions();
  }, []);

  const fetchTermsConditions = () => {
    props.hideOrShow(true);
    TermsAndConditionsClient.retrieveFile(props.user)
      .then((file) => {
        setFile(file);
        setFileFound(true);
      })
      .catch(() => {})
      .finally(() => {
        props.hideOrShow(false);
        setIsLoading(false);
      });
  };

  if (!isLoading && fileFound) {
    return (
      <div id="acceptTermsAndConditions" className="pageWrapper">
        <div className="pagePadding">
          <h1 className="pageTitle">
            {props.t("AcceptTermsConditions.header")}
          </h1>
          <div className="pageInfoWrapper">
            <InfoIconWithMessage
              message={props.t("AcceptTermsConditions.subheader")}
            />
          </div>
          <RenderPreviewT file={file} Preview={props.Preview} />
        </div>
        {file === null ? null : <AcceptanceForm {...props} file={file} />}
      </div>
    );
  } else if (!fileFound && !isLoading) {
    return (
      <h1 id="notFound" data-testid="notFound">
        {props.t("AcceptTermsConditions.docNotFound")}
      </h1>
    );
  } else {
    return null;
  }
};

export default withTranslation("emp")(AcceptTermsConditions);
